/**
 * emotionalBreathingSystem.js — FASE R6.
 *
 * Maps emotional context to a "breathing type" and compact instruction.
 *
 * Breathing types:
 *   sparse      — very short, almost silent; grief crisis or critical exhaustion
 *   restrained  — say less, resist closure; active grief
 *   compact     — brief and present; urgent/exhausted
 *   present     — warm and unhurried; pastoral-care mood
 *   reflective  — thoughtful, varied, unhurried; discernment
 *   functional  — no special breathing; logistics
 *
 * Output is ≤50 chars — a fragment, not a paragraph.
 * Phrased as natural internal instruction, not analytical description.
 */

const BREATHING_INSTRUCTIONS = {
  sparse:     "Muy breve. Solo presencia.",
  restrained: "Menos es más. Sin cierre completo.",
  compact:    "Respuesta corta. Solo presencia.",
  present:    "Abre con presencia. Sin estructura.",
  reflective: "Tono reflexivo. Varía las frases.",
  functional: null,
};

/**
 * @param {{
 *   emotion:         string,
 *   exhaustionState: string,
 *   capacity:        string,
 *   riskLevel:       string,
 *   processState:    string|null,
 * }} params
 * @returns {{ breathingType: string, instruction: string|null }}
 */
export function resolveEmotionalBreathing({ emotion = "neutral", exhaustionState = "estable", capacity = "stable", riskLevel = "none", processState = null }) {
  // Critical — barely speak
  if (riskLevel === "high" || exhaustionState === "saturación_crítica") {
    return { breathingType: "sparse", instruction: BREATHING_INSTRUCTIONS.sparse };
  }

  // Active grief — hold back
  if (emotion === "grief" || processState === "quebranto") {
    return { breathingType: "restrained", instruction: BREATHING_INSTRUCTIONS.restrained };
  }

  // Urgent or depleted — shorter, less structure
  if (
    emotion === "urgent" ||
    exhaustionState === "fatiga_moderada" ||
    exhaustionState === "agotamiento_crónico" ||
    capacity === "depleted" ||
    capacity === "overwhelmed"
  ) {
    return { breathingType: "compact", instruction: BREATHING_INSTRUCTIONS.compact };
  }

  // Active pastoral care — slow down, be present
  if (emotion === "pastoral-care" || processState === "restauración" || processState === "búsqueda") {
    return { breathingType: "present", instruction: BREATHING_INSTRUCTIONS.present };
  }

  // Reflective / deep focus
  if (emotion === "reflective" || emotion === "deep-focus" || processState === "crecimiento") {
    return { breathingType: "reflective", instruction: BREATHING_INSTRUCTIONS.reflective };
  }

  // Neutral, celebration, planning — no breathing shaping needed
  return { breathingType: "functional", instruction: null };
}
